import { useState, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type { CreateOrganisationBody, NameConflictDetails } from '@ni/shared';
import { useCreateOrganisation } from '../api/hooks';
import { useAccountForNewRecord } from './accounts';
import { nameConflictOf } from '../api/errors';
import { NameConflict, type NameConflictWords } from '../components/name-conflict/name-conflict';
import { NoteLength, noteDescribedBy } from '../components/ui/char-count';
import { ErrorLine } from '../components/ui/error-line';
import { WorkingButton } from '../components/ui/working-button';
import {
  ORGANISATIONS_TITLE,
  ORGANISATION_CANCEL,
  ORGANISATION_CONFLICT_LEAD,
  ORGANISATION_CONFLICT_NEW,
  ORGANISATION_CONFLICT_NEW_NOTE,
  ORGANISATION_CONFLICT_TAKEN,
  ORGANISATION_CREATE,
  ORGANISATION_CREATING,
  ORGANISATION_KNOW_HELP,
  ORGANISATION_KNOW_LABEL,
  ORGANISATION_NAME_LABEL,
  ORGANISATION_NEW_TITLE,
} from '../lib/copy';
import { STATEMENT_LIMIT, statementOverBy } from '../lib/prose-limit';
import { Field } from '../components/ui/field';
import { TopBar } from '../components/ui/top-bar';

/** The namesake card's nouns, in an organisation's words rather than a person's. */
const ORGANISATION_CONFLICT_WORDS: NameConflictWords = {
  lead: ORGANISATION_CONFLICT_LEAD,
  newRecord: ORGANISATION_CONFLICT_NEW,
  newRecordWorking: ORGANISATION_CREATING,
  newRecordNote: ORGANISATION_CONFLICT_NEW_NOTE,
  taken: ORGANISATION_CONFLICT_TAKEN,
  recordPath: (entityId) => `/organizations/${entityId}`,
};

/**
 * **The New Organisation form** (organisations-as-subjects note §1 R5), given
 * its two values rather than owning them — the split the intake form keeps, and
 * what makes the in-flight state testable, since it is only ever reached by
 * submitting.
 *
 * **Two boxes, and it used to be three** (owner ruling, 2026-09-09: "it should
 * also be a single prose field that undergoes extraction, just like w people. No
 * extra form field for website."). A website is something the office writes
 * down — "they are at medici.org" — and the same pass that reads the
 * relationships out of the paragraph reads the site out of it, and confirms it
 * as the same anchor the typed field used to write. A second box was a second
 * way of saying one thing, and the only one of the two that could be refused.
 *
 * **Only the name is required**, which is the one place this form deliberately
 * differs from New Person. A person entered from nothing is entered with a
 * sentence about why, because the reading of that sentence is the only thing
 * seeding the record; an organisation is not — the office may know nothing but a
 * name, and the lookup fills the rest in. So Create waits on the name and on
 * nothing else.
 *
 * `busy` is the create being in flight and it takes **every editable control**
 * with it: the name and the prose. The mutation captured the values when it
 * fired, so an edit landing mid-flight would never be filed — on success the
 * page navigates away, and on a namesake conflict the card would be asking about
 * a name that no longer matches the field. Cancel stays live: a slow create is
 * exactly when leaving is the thing wanted.
 */
export function NewOrganisationForm({
  name,
  prose,
  busy,
  deciding,
  conflict,
  error,
  failed,
  account,
  accountMissing = false,
  onName,
  onProse,
  onSubmit,
  onUse,
  onCreateNew,
}: {
  name: string;
  prose: string;
  busy: boolean;
  /**
   * **The protection account picker**, in a security org and nowhere else
   * (security fold, 2026-09-22) — under Name, as it is on the person door.
   */
  account?: ReactNode;
  /** A security org with no account chosen yet — Create is held until there is one. */
  accountMissing?: boolean;
  /** Which choice on the conflict card is in flight: an entity id, or `'new'`. */
  deciding: string | null;
  conflict: NameConflictDetails | null;
  error?: unknown;
  failed?: boolean;
  onName: (name: string) => void;
  onProse: (prose: string) => void;
  onSubmit: () => void;
  onUse: (entityId: string) => void;
  onCreateNew: () => void;
}) {
  /* The prose is optional here and capped anyway — and it is held to the
     **statement** cap, not the note cap (split-giant-note note, 2026-09-14).
     What is typed here is read *inside the request that files it*, under the
     statement's own restriction, with no splitter in front of it and no reading
     leg behind it: a paste too long to come back inside the transport's ceiling
     is still a paste this door cannot take. Only the length is refused — an
     empty box still creates. */
  const over = statementOverBy(prose);

  const canSubmit = name.trim().length > 0 && over === 0 && !accountMissing;

  return (
    <>
      <div className="form">
        <Field label={ORGANISATION_NAME_LABEL} htmlFor="no-name">
          {/* No browser autocomplete, the same call the intake form's name field
              makes: every name filed here is a new one, so a dropdown of the
              reader's own saved values has nothing to offer — and it would cover
              the namesake card that opens directly beneath. */}
          <input
            id="no-name"
            type="text"
            autoComplete="off"
            value={name}
            disabled={busy}
            onChange={(event) => onName(event.target.value)}
          />
        </Field>

        {account ?? null}

        <div className={over > 0 ? 'field over' : 'field'}>
          <label className="label" htmlFor="no-know">
            {ORGANISATION_KNOW_LABEL}
          </label>
          <textarea
            id="no-know"
            rows={4}
            value={prose}
            disabled={busy}
            aria-invalid={over > 0 || undefined}
            aria-describedby={noteDescribedBy('no-know', prose, STATEMENT_LIMIT)}
            onChange={(event) => onProse(event.target.value)}
          />
          <NoteLength id="no-know" value={prose} limit={STATEMENT_LIMIT} />
          <div className="field-note">{ORGANISATION_KNOW_HELP}</div>
        </div>
      </div>

      {conflict ? (
        <NameConflict
          details={conflict}
          pending={busy ? deciding : null}
          /* The card's two answers ARE the create, so what holds Create holds
             them too — the person door's rule, and for the same reason: the
             fields stay live while the card is up. */
          held={over > 0 || accountMissing}
          words={ORGANISATION_CONFLICT_WORDS}
          onUse={onUse}
          onCreateNew={onCreateNew}
        />
      ) : failed ? (
        <ErrorLine error={error} />
      ) : null}

      {/* While the card is up, its own two answers ARE the create — a second
          green Create beside them would be a third, unlabelled choice. */}
      <div className="form-actions">
        {conflict ? null : (
          <WorkingButton
            label={ORGANISATION_CREATE}
            workingLabel={ORGANISATION_CREATING}
            working={busy}
            disabled={!canSubmit}
            onClick={onSubmit}
          />
        )}
        <Link className="btn act" to="/organizations">
          {ORGANISATION_CANCEL}
        </Link>
      </div>
    </>
  );
}

/**
 * **What the two boxes send**, as a function for the reason `statementBody` is
 * one: the suite has no press, so what goes on the wire is asserted here rather
 * than through the button.
 *
 * An empty box is **no prose**, not an empty string: the contract's `null` is
 * what says the office did not state anything, and a `""` would be a statement
 * of nothing that the door would then have to read. `website` is always `null`
 * — the field is gone and the site comes out of the paragraph (owner ruling,
 * 2026-09-09) — and it is sent rather than omitted because the body is
 * `.strict()` and the key is not optional: what changed is who fills it in, not
 * what the wire carries.
 *
 * The override is the conflict card's two answers — adopt this record, or make a
 * new one — and it goes last, so a choice always wins over the fields it was
 * made against.
 */
export function organisationBody(
  fields: { name: string; prose: string; accountId?: string },
  override: Partial<CreateOrganisationBody> = {},
): CreateOrganisationBody {
  return {
    name: fields.name.trim(),
    website: null,
    prose: fields.prose.trim().length > 0 ? fields.prose.trim() : null,
    /* Sent only where there is one: a security org refuses the create without
       it, and every other vertical refuses the create *with* it. */
    ...(fields.accountId ? { account_id: fields.accountId } : {}),
    ...override,
  };
}

/**
 * **What holds the write.** The person door's guard in this door's terms: only
 * the length is refused here, because the prose is optional and an empty box is
 * an ordinary create.
 *
 * Editing the box already takes the namesake card down, so the card cannot be
 * standing over a paste made after the refusal — but that is coupling between
 * two handlers, not a guard. The write states its own refusal, and the card is
 * held alongside it.
 */
export function writeHeld(fields: {
  prose: string;
  writing: boolean;
  /** A security org with no protection account chosen; false everywhere else. */
  accountMissing?: boolean;
}): boolean {
  return statementOverBy(fields.prose) > 0 || fields.writing || fields.accountMissing === true;
}

/**
 * **New Organisation**, the second of R5's four manual doors.
 *
 * The create's answer *is* the seeded record, so the page it lands on has its
 * data before it mounts — the person door's rule, kept. What the office lands on
 * is the organisation's own record, addressed by the entity id, which is also
 * the record row's key.
 */
export function NewOrganisation() {
  const navigate = useNavigate();
  const create = useCreateOrganisation();
  const [params] = useSearchParams();

  /* The person door's rule, in this door's terms — see `useAccountForNewRecord`. */
  const account = useAccountForNewRecord(params.get('account_id'));

  const [name, setName] = useState('');
  const [prose, setProse] = useState('');
  /** Which choice on the conflict card is in flight: an entity id, or `'new'`. */
  const [deciding, setDeciding] = useState<string | null>(null);

  const file = (override: Partial<CreateOrganisationBody>) => {
    /* Refused before it is written, and the card's choice dropped with it: no
       write is about to settle, and `onSettled` is what normally clears it. */
    if (writeHeld({ prose, writing: create.isPending, accountMissing: account.missing })) {
      setDeciding(null);
      return;
    }
    create.mutate(organisationBody({ name, prose, accountId: account.accountId }, override), {
      onSuccess: (record) => {
        navigate(`/organizations/${record.subject.entity_id}`);
      },
      onSettled: () => setDeciding(null),
    });
  };

  /* Editing a field takes the conflict card back down: it was an answer to what
     was in the fields, and a changed field is a different question. `reset()` is
     what clears it, because the card is rendered from the mutation's own
     error. */
  const edited = () => {
    if (create.isError) create.reset();
  };

  return (
    <div className="column">
      <TopBar
        parent={{ to: '/organizations', label: ORGANISATIONS_TITLE }}
        here={ORGANISATION_NEW_TITLE}
      />

      <div className="head">
        <div className="titles">
          <div className="title">{ORGANISATION_NEW_TITLE}</div>
        </div>
      </div>

      <NewOrganisationForm
        name={name}
        prose={prose}
        busy={create.isPending}
        account={account.control}
        accountMissing={account.missing}
        deciding={deciding}
        conflict={nameConflictOf(create.error)}
        error={create.error}
        failed={create.isError}
        onName={(next) => {
          setName(next);
          edited();
        }}
        onProse={(next) => {
          setProse(next);
          edited();
        }}
        onSubmit={() => file({})}
        onUse={(entityId) => {
          setDeciding(entityId);
          file({ entity_id: entityId });
        }}
        onCreateNew={() => {
          setDeciding('new');
          file({ force_new: true });
        }}
      />
    </div>
  );
}
